import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { CurrencyDollarCircleIcon } from '@/ui/Icons/CurrencyDollarCircleIcon';
import { Multiselect } from '@/ui/Multiselect';

import { DiscoveryFilters } from '@/utils/discovery/types';
import { fetcher } from '@/utils/fetcher';
import { routes } from '@/utils/routes';

type CurrencyFilterProps = {
  filters: DiscoveryFilters;
  onSelectionComplete: (currencies?: string[]) => void;
};

const dollarLogoCurrencies = ['USD', 'AUD', 'CAD', 'COP', 'MXN'];

export const CurrencyFilter = ({ filters, onSelectionComplete }: CurrencyFilterProps) => {
  const [selectedCurrencies, setSelectedCurrencies] = useState(filters.currencies);

  const { data: currencies, isLoading } = useQuery<string[] | undefined, Error>({
    queryKey: ['discovery', 'stable-currencies'],
    queryFn: () => fetcher(routes.api.DISCOVERY_STABLE_CURRENCIES),
  });

  const items = (currencies ?? []).map((currency) => ({
    value: currency,
    label: currency,
    icon: `/images/currencies/${dollarLogoCurrencies.includes(currency) ? 'usd' : currency.toLowerCase()}.webp`,
    iconAlt: `${currency} currency`,
  }));

  // Sync local state with state.filters
  useEffect(() => setSelectedCurrencies(filters.currencies), [filters.currencies]);

  if (!items.length && !isLoading) {
    return null;
  }

  return (
    <Multiselect
      items={items}
      value={isLoading ? undefined : selectedCurrencies}
      onChange={(values) => setSelectedCurrencies(values)}
      onDropdownClose={() => onSelectionComplete(selectedCurrencies)}
      modal={true}
      triggerVariant="secondary"
      label={{ default: 'Currency', entity: 'currencies', Icon: CurrencyDollarCircleIcon }}
      multiselect={true}
      isLoading={isLoading}
    />
  );
};
